import { type MediaType } from '@atlaskit/media-common';

import { type MediaClientCache } from './cache-provider';

interface CacheWrapper<T> {
  has: (fileId: string) => boolean;
  get: (fileId: string) => T | undefined;
  set: (fileId: string, value: T) => void;
  invalidate: () => void;
}

export type MediaCacheWrapper = CacheWrapper<MediaType>;

/**
 * A wrapper for managing a scoped cache for a specific `contentId`.
 *
 * The global cache (`globalCache`) stores references to these instances,
 * allowing retrieval and centralized invalidation.
 */
export function createMediaClientCache(
  contentId: string,
  globalCache: MediaClientCache
): MediaCacheWrapper {
  const cache = new Map<string, MediaType>();

  function has(fileId: string): boolean {
    return cache.has(fileId);
  }

  function set(fileId: string, value: MediaType): void {
    cache.set(fileId, value);
  }

  function get(fileId: string): MediaType | undefined {
    return cache.get(fileId);
  }

  function invalidate(): void {
    globalCache.delete(contentId);
  }

  return { has, get, set, invalidate };
}
